import { EmptyState, ProductCard } from "@hatch-digital-nl/bravilor-storybook";
import { JSX, useLayoutEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { RouteEnum } from "../../../common/enums/RouteEnum";
import { useRefurbishedPage } from "../hooks/useRefurbishedPage";
import { useCart } from "../../cartPage/hooks/useCart";
import { canReserveProduct } from "../../cartPage/utils/canReserveProduct";
import {
    getRefurbishedProductButtonVariant,
    isReservedByCurrentUser,
} from "../utils/getRefurbishedProductButton";
import Skeleton from "react-loading-skeleton";
import { ReactComponent as CycleSvg } from "../../../common/svgs/cycle.svg";
import { ReactComponent as CheckRefurbishedSvg } from "../../../common/svgs/check-refurbished.svg";
import { getRefurbishedPricing } from "../../../common/utils/getRefurbishedPricing";
import { mapRefurbishedProductSpecifications } from "../utils/mapRefurbishedProductSpecifications";
import { useStringTranslation } from "../../../common/hooks/useStringTranslation";

const getSkeletonCount = (): number => {
    if (window.innerWidth < 1024) {
        return 4;
    }

    if (window.innerWidth < 1600) {
        return 6;
    }

    return 8;
};

export const Products = (): JSX.Element => {
    const navigate = useNavigate();
    const { refurbishedProducts, isFetching, isLoading } = useRefurbishedPage();
    const {
        pendingOrder,
        reserveProductById,
        isReservingProduct,
        timerDisplay,
    } = useCart();
    const { t } = useStringTranslation();

    const [count, setCount] = useState(getSkeletonCount);

    useLayoutEffect(() => {
        const handleResize = (): void => {
            setCount(getSkeletonCount());
        };

        handleResize();

        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    if (isLoading) {
        return (
            <div className="p-refurbished__product-cards">
                {Array.from({ length: count }, (_, index) => (
                    <Skeleton
                        key={index}
                        height="42rem"
                        borderRadius="0.8rem"
                        containerClassName="p-refurbished__product-card-skeleton"
                    />
                ))}
            </div>
        );
    }

    if (!refurbishedProducts.length)
        return (
            <EmptyState
                title={t("no-refurbished-machines-title")}
                description={t("no-refurbished-machines-description")}
                Icon={CycleSvg}
            />
        );

    return (
        <div className={"p-refurbished__product-cards"}>
            {refurbishedProducts.map((product) => {
                const buttonVariant = getRefurbishedProductButtonVariant(
                    product.state,
                    pendingOrder,
                    product.id,
                );
                const isOwnReservation = isReservedByCurrentUser(
                    product.id,
                    pendingOrder,
                );

                const buttonLabel =
                    buttonVariant === "reserved"
                        ? t("refurbished-reserved").replace(
                              "{timer}",
                              timerDisplay,
                          )
                        : buttonVariant === "reserved-other"
                          ? t("refurbished-currently-reserved")
                          : t("reserve-machine");

                return (
                    <ProductCard
                        key={product.id}
                        isBlur={isFetching}
                        className={"p-refurbished__product-card"}
                        buttonVariant={buttonVariant}
                        buttonIcon={
                            buttonVariant === "reserved"
                                ? CheckRefurbishedSvg
                                : undefined
                        }
                        title={product.product_name}
                        imageUrl={
                            product.product_images?.[0]?.converted.Medium ??
                            product.product_images?.[0]?.url ??
                            ""
                        }
                        grade={{
                            label: product.grade.label,
                            description: product.grade.description,
                        }}
                        specifications={mapRefurbishedProductSpecifications(
                            product,
                            t("art-nr"),
                        )}
                        buttonLabel={buttonLabel}
                        isButtonLoading={isReservingProduct(product.id)}
                        onCardClick={() =>
                            navigate(`${RouteEnum.REFURBISHED}/${product.id}`)
                        }
                        onClick={
                            canReserveProduct(product.state) &&
                            !isOwnReservation
                                ? () => void reserveProductById(product.id)
                                : undefined
                        }
                        pricing={{
                            ...getRefurbishedPricing(
                                product.discount_grade_price,
                                product.product_price,
                            ),
                            tooltip: {
                                title: t("price-explanation"),
                                subtitle: t("price-explanation-description"),
                            },
                        }}
                    />
                );
            })}
        </div>
    );
};
